<template>
  <div class="gs-player-section">
    <div class="gs-switch-row">
      <span class="gs-switch-row__text">
        <span class="gs-switch-row__label">{{ $t('frontend_groovevideo_videos_step1_mute_on_start') }}</span>
        <span class="gs-switch-row__help">
          Only available while autoplay is off. Autoplay always starts muted.
        </span>
      </span>
      <gd-toggle :value="model.settings.muted" @change="mute" />
    </div>

    <div class="gs-switch-row">
      <span class="gs-switch-row__text">
        <span class="gs-switch-row__label">{{ $t('frontend_groovevideo_videos_step1_auto_play') }}</span>
        <span class="gs-switch-row__help">
          Autoplay starts muted and loops in the background until a viewer clicks.
        </span>
      </span>
      <gd-toggle v-model="model.settings.autoplay" @change="autoLoad" />
    </div>

    <template v-if="model.settings.autoplay && renderComponent">
      <div class="gs-switch-row">
        <span class="gs-switch-row__text">
          <span class="gs-switch-row__label">{{ $t('frontend_groovevideo_videos_step2_begin_again') }}</span>
          <span class="gs-switch-row__help">
            Clicking the illustration restarts the video from the beginning, with sound.
          </span>
        </span>
        <gd-toggle
          v-model="model.settings.autoplay_settings.begin_again"
          @change="emitChange()"
        />
      </div>

      <div class="gs-switch-row">
        <span class="gs-switch-row__text">
          <span class="gs-switch-row__label">{{ $t('frontend_groovevideo_videos_step2_show_illustration') }}</span>
          <span class="gs-switch-row__help">
            Tells viewers the video is running without sound.
          </span>
        </span>
        <gd-toggle
          v-model="model.settings.autoplay_settings.show_illustration"
          @change="emitChange()"
        />
      </div>

      <template v-if="model.settings.autoplay_settings.show_illustration">
        <modern-prompts v-if="isModern" :model="model" kind="sound" @change="emitChange()" />
        <template v-else>
        <gs-field label="Illustration">
          <gd-dropdown
            v-model="model.settings.autoplay_settings.type"
            :options="unmuteImageOptions"
            size="full"
            class="text-left"
            @change="emitChange()"
          ></gd-dropdown>
        </gs-field>

        <gs-field v-if="model.settings.autoplay_settings.type == 1" label="Design">
          <image-library-picker
            :value="model.settings.unmute_image"
            :count="40"
            :asset-path="illustrations"
            pattern="ClickForSound_{n}.png"
            label="Select an image"
            @input="setUnmuteImage($event)"
          />
        </gs-field>

        <gs-field
          v-else-if="model.settings.autoplay_settings.type == 2"
          label="Your illustration"
          :error="customImageError"
        >
          <div class="imagePicker">
            <image-picker
              :primary="true"
              v-model="model.settings.autoplay_settings.custom_image"
              @change="emitChange()"
            ></image-picker>
          </div>
        </gs-field>

        <legacy-overlay-preview :model="model" kind="sound" />
        <div class="gs-player-section__pair">
          <gs-field label="Animation">
            <dropdown-with-scroll
              v-model="model.settings.unmute_animation"
              :options="animationDataOptions"
              class="text-left"
              max-height="150px"
              @change="reRenderComponent"
            ></dropdown-with-scroll>
          </gs-field>

          <gs-field label="Illustration size">
            <gd-dropdown
              v-model="model.settings.unmute_size"
              :options="unmuteImageSizeOptions"
              size="full"
              class="text-left"
              @change="reRenderComponent"
            ></gd-dropdown>
          </gs-field>
        </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import LegacyOverlayPreview from './LegacyOverlayPreview.vue';
import ModernPrompts from './ModernPrompts.vue';
import { isModernVideo } from './newAppearance';
import DropdownWithScroll from '@/components/DropdownWithScroll';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import { requiredIf } from 'vuelidate/lib/validators';
import ImageLibraryPicker from './ImageLibraryPicker.vue';
import { animationOptions, sizeOptions } from './playerOptions';

// webpack needs a static path here; the shared picker only calls into it.
const ILLUSTRATIONS = require.context(
  '../../../../assets/images/videos/unmute-video',
  false,
  /\.png$/
);

export default {
  name: 'AutoplaySection',

  components: { LegacyOverlayPreview, ModernPrompts, DropdownWithScroll, ImagePicker, ImageLibraryPicker },

  props: {
    model: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      // step2's local remount flag: the illustration block reads values the
      // player writes back, so it is rebuilt rather than patched.
      renderComponent: true,
      illustrations: ILLUSTRATIONS,
    };
  },

  // Ported from step2, unchanged.
  validations: {
    model: {
      settings: {
        autoplay_settings: {
          custom_image: {
            required: requiredIf(function(object) {
              return this.model.settings.autoplay && this.model.settings.autoplay_settings.show_illustration && Number(object.type) === 2;
            }),
          },
        },
      },
    },
  },

  computed: {
    isModern() { return isModernVideo(this.model); },
    animationDataOptions() {
      return animationOptions(this.$t.bind(this));
    },

    unmuteImageSizeOptions() {
      return sizeOptions(this.$t.bind(this));
    },

    unmuteImageOptions() {
      return [
        { text: this.$t('frontend_groovevideo_videos_step2_select_library'), value: 1 },
        { text: this.$t('frontend_groovevideo_videos_step2_upload_own_illustration'), value: 2 },
      ];
    },

    customImageError() {
      return this.$v.model.settings.autoplay_settings.custom_image.$error
        ? 'Choose an illustration to show while the video is muted.'
        : '';
    },
  },

  methods: {
    emitChange() {
      this.$emit('updateModel', this.model);
      this.$emit('forceRerender');
    },

    // Ported from step2.
    autoLoad() {
      if (this.model.settings.autoplay) {
        this.model.settings.autoplay_settings.begin_again = 1;
        // Disable mute externally on autoplay
        this.model.settings.muted = false;
        this.$store.dispatch('videoMute', this.model.settings.muted);
      }
      this.$store.dispatch('videoAutoLoad', this.model.settings.autoplay);
      this.$store.dispatch('videoComponentKey');
      this.emitChange();
    },

    // Ported from step2.
    mute() {
      if (!this.model.settings.autoplay) {
        // External Mute will work only when autoplay is off
        this.model.settings.muted = !this.model.settings.muted;
        this.$store.dispatch('videoMute', this.model.settings.muted);
        this.$store.dispatch('videoComponentKey');
        this.emitChange();
      }
    },

    // Ported from step2.
    reRenderComponent() {
      this.remount();
      this.emitChange();
    },

    // Ported from step2's local `forceRerender`.
    remount() {
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
    },

    // Ported from step2.
    setUnmuteImage(value) {
      this.model.settings.unmute_image = value;
      this.reRenderComponent();
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-player-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 14px;
}

.gs-player-section__pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .gs-player-section__pair {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
